import { useState } from "react";
import { Alert, Button } from "reactstrap";

import _ from "underscore";

import InviteUsers from "../CreateCloudComponents/InviteUsers";
import useTenantUsers from "../../../../api/api-hooks/tenantUsers/useTenantUsers";
import StrUtils from "../../../../utils/StrUtils";
import ListItem from "../../../../components/CRM/ListItem";

interface Props {
  values: any;
  usersKey?: string;
  onChange: (event: any) => void;
  removeUser?: (email: string) => void;
  defaultPermissions: any[];
  permissionsScope?: string;
  idKey?: string;
  currentUser?: any;
  useExistingUsers?: boolean;
  users?: any[];
}

// Cloud user management group for adding starting users for a new cloud.
// UPDATE: Can now be used for configuring users outside of the Cloud Scope.
const CloudUsersGroup = ({
  values,
  usersKey = "CloudUsers", // The key in values where the users are stored.
  onChange,
  removeUser,
  defaultPermissions,
  permissionsScope = "Cloud", // The scope of the permissions.
  idKey = "DefaultID", // The id value to use for the default permissions.
  currentUser,
  useExistingUsers = false, // Used when outside of cloud creation flow. Allows you to select from existing users.
}: Props) => {
  const [email, setEmail] = useState("");
  const [permissionID, setPermissionID] = useState<string | null>("");
  const [error, setError] = useState<string | null>(null);
  const [errorID, setErrorID] = useState<NodeJS.Timeout | null>(null);
  const { users } = useTenantUsers(!useExistingUsers);
  const hasOwner = !!_.findWhere(values[usersKey], { TenantOwner: true });

  const triggerError = (message: string) => {
    if (error) {
      errorID && clearTimeout(errorID);
      setErrorID(null);
    }

    setError(message);

    const id = setTimeout(() => {
      setError(null);
      setErrorID(null);
    }, 5000);

    setErrorID(id);
  };

  const addUser = () => {
    const permission = _.findWhere(defaultPermissions, {
      DefaultID: permissionID,
    });

    const payload = {
      Email: email,
      TenantOwner: permission?.Owner ?? false,
      PermissionsID: permissionID,
    };

    if (!StrUtils.validateEmail(email)) {
      triggerError("Invalid email address");
      return;
    }

    for (const user of values[usersKey]) {
      if (user.Email === email) {
        triggerError("User with this email is already added");
        return;
      }
    }

    onChange({
      target: {
        name: usersKey,
        value: [...values[usersKey], payload],
        invitedUser: payload,
      },
    });

    setEmail("");
    setPermissionID("");
  };

  const handleRemoveUser = (email: string) => {
    if (removeUser) {
      return removeUser(email);
    }
    const newUsers = values[usersKey].filter(
      (user: any) => user.Email !== email
    );
    onChange({ target: { name: usersKey, value: newUsers } });
  };

  return (
    <>
      <InviteUsers
        email={email}
        setEmail={setEmail}
        permissionID={permissionID || ""}
        setPermissionID={setPermissionID}
        userFilter={(user) =>
          !_.findWhere(values[usersKey], {
            Email: user?.PrimaryContactEmail ?? user?.Email,
          })
        }
        onAdd={addUser}
        permissions={defaultPermissions}
        permissionsScope={permissionsScope}
        useExistingUsers={useExistingUsers}
        useGlobalUsers={true}
        idKey={idKey}
        useOwner={!hasOwner}
        members={values[usersKey]}
        css="mb-4"
      />
      {error && (
        <Alert className="my-2" color="danger">
          {error}
        </Alert>
      )}
      <hr className="my-2 mx--3" />
      <div className="mt-3 mb--1">
        <Users
          values={values}
          users={users}
          usersKey={usersKey}
          idKey={idKey}
          defaultPermissions={defaultPermissions}
          useExistingUsers={useExistingUsers}
          currentUser={currentUser}
          handleRemoveUser={handleRemoveUser}
        />
      </div>
    </>
  );
};

interface UsersProps {
  values: any;
  usersKey: string;
  idKey: string;
  defaultPermissions: any[];
  useExistingUsers: boolean;
  currentUser?: any;
  users?: any[];
  handleRemoveUser: (email: string) => void;
}

function Users({
  values,
  usersKey,
  idKey,
  defaultPermissions,
  useExistingUsers,
  currentUser,
  users,
  handleRemoveUser,
}: UsersProps) {
  if (!values?.[usersKey]?.length) return null;

  return values?.[usersKey]?.map((user: any) => {
    const isCurrentUser = user.Email === currentUser?.PrimaryContactEmail;
    let name;
    const permissionProfile = _.findWhere(defaultPermissions, {
      [idKey]: user.PermissionsID,
    });

    if (useExistingUsers && users) {
      const foundUser = _.findWhere(users, {
        PrimaryContactEmail: user.Email,
      });

      if (foundUser) {
        name = foundUser?.Name;
      }
    }

    return (
      <ListItem
        key={user.Email}
        title={
          isCurrentUser
            ? currentUser?.Name + " (Me)"
            : name
            ? name
            : user?.Email
        }
        subTitle={isCurrentUser ? user.Email : name ? user.Email : ""}
        icon={isCurrentUser ? "mdi-account-cowboy-hat" : "mdi-account"}
        badgeTitle={permissionProfile?.Name}
        badgeColor={permissionProfile?.Owner ? "secondary" : "light"}
        optionsButton={
          <Button
            size="sm"
            className="btn-icon-only"
            color="danger"
            outline
            disabled={isCurrentUser}
            onClick={() => handleRemoveUser(user.Email)}
          >
            <i className="mdi mdi-close" />
          </Button>
        }
      />
    );
  });
}

export default CloudUsersGroup;
